/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './styles.module.scss';
import { Icon } from '@az/starc-ui';
import { Warning } from '@az/starc-ui-icons';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import usePrevious from '@/utils/usePrevious';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { type ItemAvailability } from '@/features/orders';
import { type TSkuAvailability } from '@/features/product';
import cx from 'classnames';
import CartNotificationBlock from '@/components/Cart/NotificationBlock/CartNotificationBlock';

export type Props = {
  selectedFulfillmentID: number;
  availableQuantity: number | undefined;
  selectedQuantity: number;
  nddQuantity: number | undefined;
  sddQuantity: number | undefined;
  messageIsNotification?: boolean;
  skuAvailabilityInfo?: ItemAvailability | TSkuAvailability | undefined;
  hasExceededMaxAttempt?: boolean;
};
const labelMap = {
  label_max_quantity_sdd: 'label_max_quantity_sdd',
  label_max_quantity_ndd: 'label_max_quantity_ndd',
  label_max_quantity_hd: 'label_max_quantity_hd',
  label_quantity_exceeded_ndd: 'label_quantity_exceeded_ndd',
  label_max_quantity_bopus: 'label_max_quantity_bopus',
  label_deliverymethod_unavailable: 'label_deliverymethod_unavailable',
  label_cart_lineItem_MaximumQuantityReached: 'label_cart_lineItem_MaximumQuantityReached',
};

export function MaxQuantityNotificationBlock(props: Props) {
  const labels = useLabels(labelMap);
  const { data: storeDetails } = useStoreDetailsData();
  const previousStoreNumber = usePrevious(storeDetails?.storeNumber);
  const {
    selectedFulfillmentID,
    nddQuantity = 0,
    sddQuantity = 0,
    selectedQuantity,
    availableQuantity = 0,
    messageIsNotification = false,
    skuAvailabilityInfo,
    hasExceededMaxAttempt,
  } = props;
  const locale = useLocale();

  const checkAvailability = () => {
    if (availableQuantity > selectedQuantity) {
      return true;
    }
    return false;
  };
  if (!skuAvailabilityInfo) {
    return null;
  }
  let message = '';
  if (selectedFulfillmentID == FULFILLMENT_METHODS.SAMEDAY) {
    if (
      storeDetails?.storeNumber !== previousStoreNumber &&
      sddQuantity != null &&
      sddQuantity <= 0
    ) {
      message = labels.label_deliverymethod_unavailable;
    } else if (!checkAvailability()) {
      message = labels.label_max_quantity_sdd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.NEXTDAY) {
    if (availableQuantity <= selectedQuantity && nddQuantity !== 1) {
      message = labels.label_max_quantity_ndd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.ONLINEORDER) {
    if (nddQuantity && nddQuantity > 0 && nddQuantity !== 1 && nddQuantity === selectedQuantity) {
      message = labels.label_max_quantity_ndd;
    } else if (
      nddQuantity &&
      nddQuantity > 0 &&
      nddQuantity < selectedQuantity &&
      availableQuantity > selectedQuantity
    ) {
      message = labels.label_quantity_exceeded_ndd;
    } else if (availableQuantity <= selectedQuantity) {
      message = labels.label_max_quantity_hd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.STOREORDER) {
    if (!checkAvailability()) {
      message = labels.label_max_quantity_bopus;
    }
  }

  if (locale === countryCodes.mx && !checkAvailability()) {
    message = labels.label_cart_lineItem_MaximumQuantityReached;
  }

  if (message) {
    const isUnavailableMessage = message === labels.label_deliverymethod_unavailable;

    const isMaxQuantityMessage =
      message &&
      [
        labels.label_max_quantity_ndd,
        labels.label_max_quantity_sdd,
        labels.label_quantity_exceeded_ndd,
      ].includes(message);

    const shouldShowNotificationBlockAfterMaxAttempts =
      messageIsNotification &&
      message &&
      (isUnavailableMessage || hasExceededMaxAttempt || isMaxQuantityMessage);

    return (
      <div>
        {/* Hidden aria-live region for screen reader announcements */}
        <div role="alert" className={styles.srOnly}>
          {message}
        </div>

        {/* Visible notification */}
        {shouldShowNotificationBlockAfterMaxAttempts ? (
          <CartNotificationBlock
            customClasses={cx(styles.warningTextCart, styles.alertRedesign)}
            type={'info'}
            message={message}
            itemLevel
            disableLiveRegion={true}
          />
        ) : (
          !messageIsNotification &&
          message && (
            <div className={styles.warningMessage}>
              <Icon svg={Warning} color="warning" size={5.5} />
              <p className={styles.warningTextStyles}>{message}</p>
            </div>
          )
        )}
      </div>
    );
  }
  return null;
}
